import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

/**
 * The words every Argo application shares: signing in, creating an account,
 * proving an email or phone, the step-up PIN, and the handful of nouns any
 * screen needs. A product adds its own through initI18n.
 *
 * English is the default locale; Mongolian is a choice we remember.
 */
export type Strings = Record<string, string>

const mn: Strings = {
  app: 'Argo', brand: 'Argo', brandLine: 'Төлбөр тооцоо',
  settings: 'Тохиргоо',
  signIn: 'Нэвтрэх', signOut: 'Гарах', signingIn: 'Нэвтэрч байна…',
  signInHint: 'Үргэлжлүүлэхийн тулд нэвтэрнэ үү',
  username: 'И-мэйл эсвэл нэвтрэх нэр', password: 'Нууц үг',
  showPassword: 'Нууц үг харах', hidePassword: 'Нууц үг нуух',
  devUsers: 'Туршилтын хэрэглэгч',
  errCredentials: 'Хэрэглэгчийн нэр эсвэл нууц үг буруу байна.',
  errMaybeLocked: 'Дахин буруу байна. Хэд хэдэн удаа буруу оролдвол бүртгэл түр хаагддаг тул хэсэг хүлээгээд дахин оролдоно уу.',
  errLocked: 'Хэт олон удаа буруу оролдсон тул түр хаагдлаа. Хэсэг хүлээгээд дахин оролдоно уу.',
  errIncomplete: 'Энэ бүртгэл дуусаагүй байна. Админд хандана уу.',
  errDisabled: 'Энэ бүртгэл идэвхгүй байна.',
  errUnreachable: 'Сервертэй холбогдож чадсангүй.',
  createAccount: 'Бүртгүүлэх', backToSignIn: 'Нэвтрэх рүү буцах',
  noAccountYet: 'Бүртгэлгүй юу?',
  regNo: 'Регистрийн дугаар', regNoHint: 'Жишээ нь УБ98123456',
  firstName: 'Нэр', lastName: 'Овог',
  email: 'И-мэйл', phone: 'Утасны дугаар', phoneHint: '8 оронтой дугаар',
  passwordHint: 'Хамгийн багадаа 10 тэмдэгт',
  confirmWith: 'Аль нэгээр баталгаажуулах', confirmOneHint: 'Нэгийг нь одоо баталгаажуулна; нөгөөг нь дараа Тохиргооноос хийж болно.',
  regCheckEmail: 'И-мэйлээ шалгана уу', regSentTo: '{{email}} хаяг руу холбоос илгээлээ.',
  regLinkOnce: 'Холбоос нэг удаа ажиллах бөгөөд 24 цагийн дараа хүчингүй болно.',
  confirmPhone: 'Утсаа баталгаажуулах',
  sendThis: 'Энэ кодыг', toShortcode: '{{shortcode}} дугаар руу илгээнэ үү',
  openMessages: 'Мессеж бичих', smsCost: 'Нэг мессеж 150₮ төлбөртэй.',
  waitingForSms: 'Хүлээж байна… {{seconds}} секунд',
  codeExpired: 'Хугацаа дууслаа. Дахин эхлүүлнэ үү.',
  phoneConfirmed: 'Утас баталгаажлаа', emailConfirmed: 'И-мэйл баталгаажлаа',
  accountReady: 'Таны данс: {{account}}',
  confirming: 'Баталгаажуулж байна…',
  channelUnavailable: 'Одоогоор зөвхөн нэг арга боломжтой байна.',
  errPhoneLogin: 'Утасны дугаараар нэвтрэх боломжгүй. И-мэйл хаягаа оруулна уу.',
  usernameHint: '3-32 тэмдэгт. Арилжааны захиалгад энэ нэр бусдад харагдана.',
  nameFree: '{{name}} — авах боломжтой',
  contactDetails: 'Холбоо барих мэдээлэл',
  verified: 'Баталгаажсан', unverified: 'Баталгаажаагүй',
  confirmNow: 'Баталгаажуулах', changeContact: 'Солих', sendCode: 'Илгээх',
  linkSentTo: '{{email}} хаяг руу холбоос илгээлээ.',
  changeKeepsOldUntilConfirmed: 'Шинэ утга баталгаажих хүртэл хуучин нь хэвээр ажиллана.',
  createAccountLede: 'Хэдэн мэдээлэл оруулаад, и-мэйл эсвэл утсаа баталгаажуулна.',
  haveAccount: 'Бүртгэлтэй юу?',
  groupIdentity: 'Таны мэдээлэл', groupContact: 'Холбоо барих', groupSecurity: 'Нэвтрэх',
  forgotPassword: 'Нууц үгээ мартсан?',
  forgotTitle: 'Нууц үг сэргээх',
  forgotLede: 'Бүртгэлдээ хэрэглэсэн и-мэйл эсвэл утсаараа баталгаажуулна уу.',
  forgotEmailHint: 'Бүртгүүлэхдээ хэрэглэсэн хаяг',
  forgotPhoneHint: 'Бүртгүүлэхдээ хэрэглэсэн дугаар',
  sendResetLink: 'Үргэлжлүүлэх',
  checkYourEmail: 'И-мэйлээ шалгана уу',
  resetSentTo: 'Хэрэв {{email}} бүртгэлтэй бол холбоос илгээгдсэн.',
  resetLinkOnce: 'Холбоос нэг удаа ажиллах бөгөөд 1 цагийн дараа хүчингүй болно.',
  chooseNewPassword: 'Шинэ нууц үг', chooseNewLede: 'Өмнөх нууц үгээ дахин ашиглах боломжгүй.',
  newPassword: 'Шинэ нууц үг', repeatPassword: 'Дахин оруулна уу',
  passwordsDiffer: 'Хоёр нууц үг таарахгүй байна.',
  setPassword: 'Хадгалах',
  passwordChanged: 'Нууц үг солигдлоо', signInWithNew: 'Шинэ нууц үгээрээ нэвтэрнэ үү.',
  account: 'Данс', balance: 'Үлдэгдэл', amount: 'Дүн',
  pin: 'ПИН', submit: 'Илгээх', cancel: 'Цуцлах', save: 'Хадгалах',
  back: 'Буцах', home: 'Эхлэл', done: 'Амжилттай', error: 'Алдаа',
  noData: 'Мэдээлэл алга', language: 'Хэл', theme: 'Загвар',
  light: 'Цайвар', dark: 'Бараан', system: 'Систем',
  loading: 'Ачаалж байна…', refresh: 'Шинэчлэх',
  price: 'Үнэ', total: 'Нийт',
  time: 'Цаг', block: 'Блок', txid: 'Гүйлгээний дугаар',
  pinRequired: 'ПИН шаардлагатай',
  irreversible: 'Энэ захиалга блокчэйнд бичигдэнэ. Биелсэн гүйлгээг буцаах боломжгүй.',
  confirm: 'Баталгаажуулах',
  security: 'Аюулгүй байдал',
  roles: 'Эрх',
  history: 'Түүх', status: 'Төлөв', verify: 'Шалгах',
  apiDown: 'Сервер холбогдохгүй байна',
}

const en: Strings = {
  app: 'Argo', brand: 'Argo', brandLine: 'Settlement',
  settings: 'Settings',
  signIn: 'Sign in', signOut: 'Sign out', signingIn: 'Signing in…',
  signInHint: 'Sign in to continue',
  username: 'Email or username', password: 'Password',
  showPassword: 'Show password', hidePassword: 'Hide password',
  devUsers: 'Test users',
  errCredentials: 'That username or password is not right.',
  errMaybeLocked: 'Still not right. Repeated failures lock the account for a while, so wait a moment before trying again.',
  errLocked: 'Too many attempts, so this account is locked for a while. Try again shortly.',
  errIncomplete: 'This account is not fully set up. Contact an administrator.',
  errDisabled: 'This account is disabled.',
  errUnreachable: 'Could not reach the server.',
  createAccount: 'Create account', backToSignIn: 'Back to sign in',
  noAccountYet: 'No account yet?',
  regNo: 'Registration number', regNoHint: 'For example УБ98123456',
  firstName: 'First name', lastName: 'Last name',
  email: 'Email', phone: 'Phone', phoneHint: 'Eight digits',
  passwordHint: 'At least 10 characters',
  confirmWith: 'Confirm with', confirmOneHint: 'One is enough to start. You can confirm the other later in Settings.',
  regCheckEmail: 'Check your email', regSentTo: 'A link is on its way to {{email}}.',
  regLinkOnce: 'It works once and expires in 24 hours.',
  confirmPhone: 'Confirm your phone',
  sendThis: 'Text this code', toShortcode: 'to {{shortcode}}',
  openMessages: 'Open Messages', smsCost: 'Each message costs 150₮.',
  waitingForSms: 'Waiting… {{seconds}}s left',
  codeExpired: 'That code expired. Start again to get a new one.',
  phoneConfirmed: 'Phone confirmed', emailConfirmed: 'Email confirmed',
  accountReady: 'Your account is {{account}}.',
  confirming: 'Confirming…',
  channelUnavailable: 'Only one way of confirming is available right now.',
  errPhoneLogin: 'You cannot sign in with a phone number. Use your email address instead.',
  usernameHint: '3-32 characters. Other traders see this name on the order book.',
  nameFree: '{{name}} is available',
  contactDetails: 'Contact details',
  verified: 'Confirmed', unverified: 'Not confirmed',
  confirmNow: 'Confirm', changeContact: 'Change', sendCode: 'Send',
  linkSentTo: 'A link is on its way to {{email}}.',
  changeKeepsOldUntilConfirmed: 'The old one keeps working until the new one is confirmed.',
  createAccountLede: 'A few details, then confirm your email or your phone.',
  haveAccount: 'Already have an account?',
  groupIdentity: 'Who you are', groupContact: 'How to reach you', groupSecurity: 'Signing in',
  forgotPassword: 'Forgot your password?',
  forgotTitle: 'Reset your password',
  forgotLede: 'Confirm the email or phone your account already uses.',
  forgotEmailHint: 'The address you registered with',
  forgotPhoneHint: 'The number you registered with',
  sendResetLink: 'Continue',
  checkYourEmail: 'Check your email',
  resetSentTo: 'If {{email}} is registered, a link is on its way.',
  resetLinkOnce: 'It works once and expires in an hour.',
  chooseNewPassword: 'Choose a new password', chooseNewLede: 'It cannot be one you have used before.',
  newPassword: 'New password', repeatPassword: 'Repeat it',
  passwordsDiffer: 'Those two do not match.',
  setPassword: 'Save password',
  passwordChanged: 'Password changed', signInWithNew: 'Sign in with your new password.',
  account: 'Account', balance: 'Balance', amount: 'Amount',
  pin: 'PIN', submit: 'Submit', cancel: 'Cancel', save: 'Save',
  back: 'Back', home: 'Home', done: 'Done', error: 'Error',
  noData: 'Nothing yet', language: 'Language', theme: 'Theme',
  light: 'Light', dark: 'Dark', system: 'System',
  loading: 'Loading…', refresh: 'Refresh',
  price: 'Price', total: 'Total',
  time: 'Time', block: 'Block', txid: 'Tx ID',
  pinRequired: 'PIN required',
  irreversible: 'This order is written to the ledger. Once it fills, it cannot be undone.',
  confirm: 'Confirm',
  security: 'Security',
  roles: 'Roles',
  history: 'History', status: 'Status', verify: 'Verify',
  apiDown: 'Backend unreachable',
}

/** Start i18next with the shared strings plus this application's own. */
export function initI18n(extra: { mn: Strings; en: Strings } = { mn: {}, en: {} }) {
  let saved = 'en'
  try { saved = localStorage.getItem('argo.lang') || 'en' } catch { /* private mode */ }

  void i18n.use(initReactI18next).init({
    resources: {
      mn: { translation: { ...mn, ...extra.mn } },
      en: { translation: { ...en, ...extra.en } },
    },
    lng: saved,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  })
  return i18n
}

export function setLanguage(lng: 'mn' | 'en') {
  void i18n.changeLanguage(lng)
  try { localStorage.setItem('argo.lang', lng) } catch { /* private mode */ }
}

export default i18n
